import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardActionArea,
    Grid,
    Chip,
    IconButton,
    CircularProgress,
    Breadcrumbs,
    Link,
    Avatar,
    alpha,
    useTheme
} from '@mui/material';
import {
    Add as AddIcon,
    NavigateNext as NavigateNextIcon,
    EmojiEvents as TrophyIcon,
    Edit as EditIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import AdminLayout from 'pages/admin/components/AdminLayout';
import { getAllAchievements, Achievement } from 'services/achievementService';

const AchievementsStudioPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [achievements, setAchievements] = useState<Achievement[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);

    useEffect(() => {
        fetchAchievements();
    }, []);

    useEffect(() => {
        if (id && achievements.length > 0) {
            const achievement = achievements.find(a => a.id === parseInt(id));
            setSelectedAchievement(achievement || null);
        } else {
            setSelectedAchievement(null);
        }
    }, [id, achievements]);

    const fetchAchievements = async () => {
        setLoading(true);
        try {
            const data = await getAllAchievements();
            setAchievements(data);
        } catch (error) {
            console.error('Error fetching achievements:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => {
        navigate('/admin/achievements/new');
    };

    const getTypeColor = (typeKey: string | undefined) => {
        if (!typeKey) return theme.palette.primary.main;

        switch (typeKey.toLowerCase()) {
            case 'problem_count': return theme.palette.success.main;
            case 'session_completion': return theme.palette.info.main;
            case 'streak': return theme.palette.warning.main;
            case 'perfect_score': return theme.palette.secondary.main;
            default: return theme.palette.primary.main;
        }
    };

    // Grid view when no achievement selected
    if (!id) {
        return (
            <AdminLayout>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box>
                            <Typography variant="h4" fontWeight="bold" gutterBottom>
                                Achievements
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Manage badges and rewards for student accomplishments
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleCreateNew}
                            disabled={loading}
                        >
                            New Achievement
                        </Button>
                    </Box>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {achievements.map((achievement) => (
                                <Grid item xs={12} sm={6} md={4} key={achievement.id}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            height: '100%',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                boxShadow: 4,
                                                transform: 'translateY(-4px)'
                                            }
                                        }}
                                    >
                                        <CardActionArea onClick={() => navigate(`/admin/achievements/${achievement.id}`)}>
                                            <CardContent>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                                    <Avatar
                                                        sx={{
                                                            width: 56,
                                                            height: 56,
                                                            bgcolor: alpha(getTypeColor(achievement.typeKey), 0.1),
                                                            color: getTypeColor(achievement.typeKey),
                                                            mr: 2
                                                        }}
                                                    >
                                                        <TrophyIcon />
                                                    </Avatar>
                                                    <Box sx={{ flexGrow: 1 }}>
                                                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                                                            {achievement.name}
                                                        </Typography>
                                                        <Chip
                                                            label={achievement.typeKey || 'Achievement'}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: alpha(getTypeColor(achievement.typeKey), 0.1),
                                                                color: getTypeColor(achievement.typeKey),
                                                                fontWeight: 'bold',
                                                                fontSize: '0.7rem'
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    {achievement.description}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Chip
                                                        label={achievement.typeKey || 'N/A'}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                    {achievement.thresholdValue && (
                                                        <Chip
                                                            label={`Threshold: ${achievement.thresholdValue}`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            </AdminLayout>
        );
    }

    // Detail view when achievement selected
    return (
        <AdminLayout>
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/admin/achievements');
                        }}
                        sx={{ cursor: 'pointer' }}
                    >
                        All Achievements
                    </Link>
                    {selectedAchievement && (
                        <Typography color="text.primary" fontWeight="medium">
                            {selectedAchievement.name}
                        </Typography>
                    )}
                </Breadcrumbs>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : selectedAchievement ? (
                <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                    <Card variant="outlined" sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <Avatar
                                sx={{
                                    width: 80,
                                    height: 80,
                                    bgcolor: alpha(getTypeColor(selectedAchievement.typeKey), 0.1),
                                    color: getTypeColor(selectedAchievement.typeKey),
                                    mr: 3
                                }}
                            >
                                <TrophyIcon sx={{ fontSize: 40 }} />
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h4" fontWeight="bold" gutterBottom>
                                    {selectedAchievement.name}
                                </Typography>
                                <Chip
                                    label={selectedAchievement.typeKey || 'Achievement'}
                                    sx={{
                                        bgcolor: alpha(getTypeColor(selectedAchievement.typeKey), 0.1),
                                        color: getTypeColor(selectedAchievement.typeKey),
                                        fontWeight: 'bold'
                                    }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton color="primary">
                                    <EditIcon />
                                </IconButton>
                                <IconButton color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        <Typography variant="body1" color="text.secondary" paragraph>
                            {selectedAchievement.description}
                        </Typography>

                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Type
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {selectedAchievement.typeKey}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Threshold Value
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {selectedAchievement.thresholdValue || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Icon
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {selectedAchievement.iconName}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Created
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {new Date(selectedAchievement.createdAt).toLocaleDateString()}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Card>
                </Box>
            ) : (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                    <Typography color="error">Achievement not found</Typography>
                </Box>
            )}
        </AdminLayout>
    );
};

export default AchievementsStudioPage;
